import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
  Typography,
} from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { PortalShell } from "../components/PortalShell";
import { formatMxLong } from "../utils/dateMx";
import { labelAppointmentModality } from "../utils/consultationModes";

function parseUtc(s) {
  if (!s) return null;
  const t = String(s).endsWith("Z") ? String(s) : `${String(s)}Z`;
  return new Date(t);
}

export default function PortalPsychologistAppointments() {
  const { user, isAuthenticated } = usePortalAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = () => {
    setLoading(true);
    portalApi
      .psychologistAppointments()
      .then((r) => setRows(r.appointments || []))
      .catch(() => toast.error("No se pudieron cargar las citas"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const up = [];
    const pa = [];
    for (const a of rows || []) {
      const t = parseUtc(a.startUtc);
      const ms = t ? t.getTime() : 0;
      if (ms >= now) {
        up.push(a);
      } else {
        pa.push(a);
      }
    }
    up.sort((a, b) => String(a.startUtc).localeCompare(String(b.startUtc)));
    pa.sort((a, b) => String(b.startUtc).localeCompare(String(a.startUtc)));
    return { upcoming: up, past: pa };
  }, [rows]);

  function askCancel(id) {
    setPendingCancel(id);
    setCancelOpen(true);
  }

  async function confirmCancel() {
    if (!pendingCancel) return;
    setCancelling(true);
    try {
      await portalApi.cancelAppointment(pendingCancel);
      toast.success("Cita cancelada");
      setCancelOpen(false);
      setPendingCancel(null);
      load();
    } catch (e) {
      toast.error(e.message || "No se pudo cancelar");
    } finally {
      setCancelling(false);
    }
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== "psychologist") {
    if (user?.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  function rowCard(a, { showCancel }) {
    const name =
      (a.clientName && String(a.clientName).trim()) || "Paciente";
    return (
      <li key={a.appointmentId}>
        <Card className="border border-brand-brown/15 shadow-sm overflow-hidden">
          <CardBody className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Typography className="font-semibold text-brand-dark text-lg">
                  {name}
                </Typography>
                {a.modality && (
                  <Chip
                    size="sm"
                    variant="ghost"
                    value={labelAppointmentModality(a.modality)}
                    className="!bg-slate-100 !text-slate-700 normal-case"
                  />
                )}
                <Chip
                  size="sm"
                  variant="ghost"
                  value={a.status === "cancelled" ? "Cancelada" : "Programada"}
                  className={
                    a.status === "cancelled"
                      ? "!bg-stone-100 !text-stone-600 normal-case"
                      : "!bg-brand-lavender/15 !text-brand-navy normal-case"
                  }
                />
              </div>
              {a.clientEmail && (
                <p className="text-sm text-brand-gray break-all">{a.clientEmail}</p>
              )}
              <p className="text-sm text-brand-dark mt-1">{formatMxLong(a.startUtc)}</p>
            </div>
            {showCancel && a.status !== "cancelled" && (
              <Button
                size="sm"
                variant="outlined"
                className="border-red-200 text-red-600 hover:border-red-400 shrink-0 normal-case w-full sm:w-auto"
                onClick={() => askCancel(a.appointmentId)}
              >
                Cancelar cita
              </Button>
            )}
          </CardBody>
        </Card>
      </li>
    );
  }

  return (
    <PortalShell
      title="Mis citas"
      subtitle="Citas con pacientes: pendientes y recientes."
    >
      {loading && (
        <div className="flex items-center gap-3 text-brand-gray py-12 justify-center">
          <Spinner className="h-8 w-8" />
          <span>Cargando citas…</span>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="border border-dashed border-brand-brown/30 bg-white/70">
          <CardBody className="py-12 text-center px-6">
            <Typography className="text-brand-dark font-medium mb-1">
              Aún no hay citas
            </Typography>
            <p className="text-sm text-brand-gray">
              Cuando un paciente reserve contigo, aparecerá aquí.
            </p>
          </CardBody>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-10">
          <section>
            <Typography className="font-semibold text-brand-dark mb-3">
              Próximas
            </Typography>
            {upcoming.length === 0 ? (
              <p className="text-sm text-brand-gray">No tienes citas programadas próximas.</p>
            ) : (
              <ul className="space-y-4">
                {upcoming.map((a) =>
                  rowCard(a, { showCancel: a.status !== "cancelled" })
                )}
              </ul>
            )}
          </section>
          <section>
            <Typography className="font-semibold text-brand-dark mb-3">
              Anteriores
            </Typography>
            {past.length === 0 ? (
              <p className="text-sm text-brand-gray">No hay sesiones pasadas registradas.</p>
            ) : (
              <ul className="space-y-4">{past.map((a) => rowCard(a, { showCancel: false }))}</ul>
            )}
          </section>
        </div>
      )}

      <Dialog
        open={cancelOpen}
        handler={setCancelOpen}
        size="sm"
        className="!max-w-[calc(100vw-2rem)]"
      >
        <DialogHeader className="text-brand-dark">¿Cancelar esta cita?</DialogHeader>
        <DialogBody className="text-brand-gray text-sm">
          El paciente verá la sesión como cancelada y el horario quedará libre.
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="text"
            className="text-brand-gray normal-case"
            onClick={() => setCancelOpen(false)}
            disabled={cancelling}
          >
            Volver
          </Button>
          <Button
            className="bg-red-500 text-white normal-case"
            onClick={confirmCancel}
            disabled={cancelling}
          >
            {cancelling ? "Cancelando…" : "Sí, cancelar"}
          </Button>
        </DialogFooter>
      </Dialog>
    </PortalShell>
  );
}
